<template>
  <span
    :data-label="label"
    class="before:bg-white after:bg-white"
    :class="[
      newColor,
      newPosition,
      size,
      {
        'd-tooltip': active,
        'is-square': square,
        'is-animated': newAnimated,
        'is-always': always,
        'is-multiline': multilined,
        'is-dashed': dashed,
      },
    ]"
  >
    <slot />
  </span>
</template>

<script>
/**
 * @displayName DTooltip
 */
export default {
  name: "DTooltip",
  props: {
    /**
     * Tooltip text
     */
    label: String,

    /**
     * Set as `true` to keep tooltip open, or to open programmatically.
     */
    active: {
      type: Boolean,
      default: true,
    },

    /**
     * Any color is available as long as it is declared at the configuration and tailwind level.
     */
    color: {
      type: String,
      default: "text-primary",
    },

    /**
     * Position of the tooltip.
     * @parameters top, bottom, left, right
     */
    position: {
      type: String,
      default: "top",
      validator(value) {
        return ["top", "bottom", "left", "right"].indexOf(value) > -1;
      },
    },

    /**
     * Tooltip will be always active
     */
    always: {
      type: Boolean,
      default: false,
    },

    /**
     * Tooltip will have a little fade animation
     */
    animated: {
      type: Boolean,
      default: true,
    },

    /**
     * Tooltip will be square (not rounded corners)
     */
    square: {
      type: Boolean,
      default: false,
    },

    /**
     * Tooltip slot will have a dashed underline
     */
    dashed: {
      type: Boolean,
      default: false,
    },

    /**
     * Tooltip will be multilined
     */
    multilined: {
      type: Boolean,
      default: false,
    },

    /**
     * Tooltip multiline size. Controls the width of the multiline. (only works for multilined tooltips)
     * @parameters small, medium, large
     */
    size: {
      type: String,
      default: "medium",
      validator: (value) => ["small", "medium", "large"].indexOf(value) > -1,
    },
  },

  computed: {
    newColor() {
      return `d-tooltip-${this.color}`;
    },

    newAnimated() {
      return this.animated;
    },

    newPosition() {
      return `is-${this.position}`;
    },
  },
};
</script>

<style scoped>
/* Begins is-top */
.d-tooltip.is-top:before,
.d-tooltip.is-top:after {
  top: auto;
  right: auto;
  bottom: calc(100% + 5px + 2px);
  left: 50%;
  transform: translateX(-50%);
}

.d-tooltip.is-top.is-multiline.small:after {
  width: 180px;
}
.d-tooltip.is-top.is-multiline.medium:after {
  width: 240px;
}
.d-tooltip.is-top.is-multiline.large:after {
  width: 300px;
}
/* Ends is-top */

/* Begins is-right */
.d-tooltip.is-right:before,
.d-tooltip.is-right:after {
  top: 50%;
  right: auto;
  bottom: auto;
  left: calc(100% + 5px + 2px);
  transform: translateY(-50%);
}

.d-tooltip.is-right.is-multiline.small:after {
  width: 180px;
}
.d-tooltip.is-right.is-multiline.medium:after {
  width: 240px;
}
.d-tooltip.is-right.is-multiline.large:after {
  width: 300px;
}
/* Ends is-right */

/* Begins is-bottom */
.d-tooltip.is-bottom:before,
.d-tooltip.is-bottom:after {
  top: calc(100% + 5px + 2px);
  right: auto;
  bottom: auto;
  left: 50%;
  transform: translateX(-50%);
}

.d-tooltip.is-bottom.is-multiline.small:after {
  width: 180px;
}
.d-tooltip.is-bottom.is-multiline.medium:after {
  width: 240px;
}
.d-tooltip.is-bottom.is-multiline.large:after {
  width: 300px;
}
/* Ends is-bottom */

/* Begins is-left */
.d-tooltip.is-left:before,
.d-tooltip.is-left:after {
  top: 50%;
  right: calc(100% + 5px + 2px);
  bottom: auto;
  left: auto;
  transform: translateY(-50%);
}

.d-tooltip.is-left.is-multiline.small:after {
  width: 180px;
}
.d-tooltip.is-left.is-multiline.medium:after {
  width: 240px;
}
.d-tooltip.is-left.is-multiline.large:after {
  width: 300px;
}
/* Ends is-left */

.d-tooltip {
  position: relative;
  display: inline-flex;
}

.d-tooltip:before,
.d-tooltip:after {
  position: absolute;
  content: "";
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.d-tooltip:before {
  z-index: 889;
}

.d-tooltip:after {
  content: attr(data-label);
  width: auto;
  padding: 0.35rem 0.75rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 400;
  box-shadow: 0 1px 2px 1px rgba(0, 1, 0, 0.2);
  z-index: 888;
  white-space: nowrap;
}

.d-tooltip:not([data-label=""]):hover:before,
.d-tooltip:not([data-label=""]):hover:after {
  opacity: 1;
  visibility: visible;
}

.d-tooltip:not([data-label=""]).is-always:before,
.d-tooltip:not([data-label=""]).is-always:after {
  opacity: 1;
  visibility: visible;
}

.d-tooltip.small:after {
  padding: 0.25rem 0.65rem;
  border-radius: 7px;
  font-size: 0.62rem;
}

.d-tooltip.large:after {
  padding: 0.45rem 0.85rem;
  font-size: 1.2rem;
}

.d-tooltip.is-multiline:after {
  display: flex-block;
  text-align: center;
  white-space: normal;
}

.d-tooltip.is-dashed {
  border-bottom: 1px dashed var(--color-grey-300);
  cursor: default;
}

.d-tooltip.is-square:after {
  border-radius: 0;
}

.d-tooltip.is-animated:before,
.d-tooltip.is-animated:after {
  transition: opacity 100ms ease-in-out, visibility 100ms ease-in-out;
}
</style>
